/**
 * Customer Reviews dashboard state.
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getFeedbackSummaryApi } from '../../services/customerFeedbackApi';

export const fetchFeedbackSummary = createAsyncThunk(
    'customerFeedback/fetchSummary',
    // `from` / `to` are optional ISO dates passed straight through, so the caller owns
    // the window and the slice does not have to know the dashboard's default.
    async ({ from, to } = {}, { rejectWithValue }) => {
        try {
            return await getFeedbackSummaryApi({ from, to });
        } catch (error) {
            // The server's message names which date is wrong. A generic string would
            // throw away the only thing telling the user what to fix.
            const message = error.response?.data?.message
                || error.response?.data?.error
                || 'Failed to load customer reviews.';
            return rejectWithValue(message);
        }
    }
);

const initialState = {
    questions: [],
    overallScore: null,
    responseCount: 0,
    loading: false,
    error: null,
};

const customerFeedbackSlice = createSlice({
    name: 'customerFeedback',
    initialState,
    reducers: {
        clearFeedbackError(state) {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchFeedbackSummary.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchFeedbackSummary.fulfilled, (state, action) => {
                state.loading = false;
                state.questions = action.payload?.questions ?? [];
                state.overallScore = action.payload?.overallScore ?? null;
                state.responseCount = action.payload?.responseCount ?? 0;
            })
            .addCase(fetchFeedbackSummary.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to load customer reviews.';
                // Deliberately NOT clearing questions: a failed refresh should leave the
                // last good numbers on screen under an error banner, rather than
                // replacing them with an empty dashboard that reads as "no reviews".
            });
    },
});

export const { clearFeedbackError } = customerFeedbackSlice.actions;

export const selectFeedbackQuestions = (state) => state.customerFeedback.questions;
export const selectFeedbackOverallScore = (state) => state.customerFeedback.overallScore;
export const selectFeedbackResponseCount = (state) => state.customerFeedback.responseCount;
export const selectFeedbackLoading = (state) => state.customerFeedback.loading;
export const selectFeedbackError = (state) => state.customerFeedback.error;

export default customerFeedbackSlice.reducer;
